import { useEffect, useRef, useState } from "react";
import PhotoAlbum from "react-photo-album";
import styles from "./ImageGallery.module.css";
import { Header, Title, Footer } from "../components"; // Added Footer

// Consider adding a Lightbox for a better viewing experience
// import Lightbox from "yet-another-react-lightbox";
// import "yet-another-react-lightbox/styles.css";

type FacebookFeedItem = {
  full_picture?: string; // Make optional as not all feed items are photos
  id: string;
  created_time: string;
  message?: string; // Make optional
  permalink_url: string;
  attachments?: {
    // For posts with multiple images or videos
    data: {
      media?: {
        image: {
          src: string;
          height: number;
          width: number;
        };
      };
      subattachments?: {
        // For carousel posts
        data: {
          media: {
            image: {
              src: string;
              height: number;
              width: number;
            };
          };
          description?: string; // Message for subattachment
        }[];
      };
      description?: string; // Message for single attachment
      type?: string; // e.g., 'photo', 'album', 'video'
    }[];
  };
};

type FacebookGalleryResponse = {
  data: FacebookFeedItem[];
  // Include paging if you plan to implement pagination
};

type PhotoGalleryItem = {
  src: string;
  width: number;
  height: number;
  message?: string;
  key?: string; // For React Photo Album
};

type StoredGalleryResponse = {
  response: FacebookGalleryResponse;
  date: string;
};

export const ImageGallery = () => {
  const [photos, setPhotos] = useState<PhotoGalleryItem[] | null>(null);
  // const [lightboxOpen, setLightboxOpen] = useState(false); // For lightbox
  // const [lightboxIndex, setLightboxIndex] = useState(0);   // For lightbox

  const [inModal, setInModal] = useState(false); // Keeping your original modal for now
  const [modalSrc, setModalSrc] = useState("");
  const [modalText, setModalText] = useState("");
  const modalRef = useRef<HTMLDivElement | null>(null);

  const facebookPageId = "104748565334108";
  const accessCode =
    process.env.REACT_APP_HYDROCLEAN_FB_ACCESS_TOKEN ||
    process.env.REACT_APP_MASSARI_FB_ACCESS_TOKEN; // Use appropriate token
  // Requesting 'attachments' to get images from posts that aren't direct photo uploads
  const requestString = `https://graph.facebook.com/v20.0/${facebookPageId}/feed?limit=50&fields=full_picture,created_time,message,permalink_url,attachments{description,media,subattachments,type}&access_token=${accessCode}`;

  const processFbDataToPhotos = async (
    fbData: FacebookFeedItem[],
  ): Promise<PhotoGalleryItem[]> => {
    const photoPromises: Promise<PhotoGalleryItem | null>[] = [];

    fbData.forEach((item) => {
      const itemMessage = item.message;

      // 1. Direct full_picture (no dimensions available, need to load)
      if (item.full_picture) {
        photoPromises.push(
          createPhotoGalleryItem(
            item.full_picture,
            itemMessage || item.id,
            item.id + "_full",
          ),
        );
      }
      // 2. Attachments
      else if (item.attachments?.data) {
        item.attachments.data.forEach((attachment, attIdx) => {
          // Single media in attachment
          if (attachment.media?.image?.src) {
            photoPromises.push(
              createPhotoGalleryItem(
                attachment.media.image.src,
                itemMessage || attachment.description || item.id,
                `${item.id}_att_${attIdx}`,
                attachment.media.image.width,
                attachment.media.image.height,
              ),
            );
          }
          // Subattachments (carousel posts)
          else if (attachment.subattachments?.data) {
            attachment.subattachments.data.forEach((sub, subIdx) => {
              if (sub.media?.image?.src) {
                photoPromises.push(
                  createPhotoGalleryItem(
                    sub.media.image.src,
                    itemMessage || sub.description || item.id,
                    `${item.id}_sub_${attIdx}_${subIdx}`,
                    sub.media.image.width,
                    sub.media.image.height,
                  ),
                );
              }
            });
          }
        });
      }
    });
    const results = await Promise.all(photoPromises);
    return results.filter((p) => p !== null) as PhotoGalleryItem[];
  };

  const createPhotoGalleryItem = (
    src: string,
    message: string | undefined,
    key: string,
    width?: number,
    height?: number,
  ): Promise<PhotoGalleryItem | null> => {
    // If dimensions are provided from API, use them directly
    if (width && height) {
      return Promise.resolve({
        src,
        width,
        height,
        message,
        key,
      });
    }

    // Otherwise, load the image to get dimensions
    return new Promise((resolve) => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        resolve({
          src,
          width: image.naturalWidth || 16,
          height: image.naturalHeight || 9,
          message,
          key,
        });
      };
      image.onerror = () => {
        console.warn("Failed to load image:", src);
        resolve(null);
      };
    });
  };

  useEffect(() => {
    const todayDate = new Date();
    const storageItem = localStorage.getItem("gallery");
    const savedGallery: StoredGalleryResponse | null = storageItem
      ? JSON.parse(storageItem)
      : null;

    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    if (savedGallery && isSameDay(new Date(savedGallery.date), todayDate)) {
      console.log("Getting local gallery");
      processFbDataToPhotos(savedGallery.response.data).then(setPhotos);
    } else {
      fetch(requestString)
        .then((response) => {
          if (!response.ok) {
            return response.json().then((errData) => {
              console.error(
                `Error fetching gallery. Status: ${response.status}`,
                errData,
              );
              if (savedGallery?.response?.data) {
                processFbDataToPhotos(savedGallery.response.data).then(
                  setPhotos,
                );
                console.warn("Displayed stale gallery due to fetch error.");
              } else {
                setPhotos([]);
              }
              throw new Error(
                `Facebook API Error: ${
                  errData?.error?.message || response.statusText
                }`,
              );
            });
          }
          return response.json();
        })
        .then((res: FacebookGalleryResponse) => {
          if (res.data) {
            processFbDataToPhotos(res.data).then((images) => {
              setPhotos(images);
              const storedObj: StoredGalleryResponse = {
                response: { data: res.data }, // Store the relevant part of the response
                date: todayDate.toString(),
              };
              localStorage.setItem("gallery", JSON.stringify(storedObj));
            });
          } else {
            console.error(
              "Fetched gallery successfully, but data is missing:",
              res,
            );
            if (savedGallery?.response?.data) {
              processFbDataToPhotos(savedGallery.response.data).then(setPhotos);
              console.warn("Displayed stale gallery due to fetch data issue.");
            } else {
              setPhotos([]);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch or process gallery:", err);
          if (savedGallery?.response?.data && !photos) {
            processFbDataToPhotos(savedGallery.response.data).then(setPhotos);
            console.warn(
              "Displayed stale gallery due to critical fetch error.",
            );
          } else if (!photos) {
            setPhotos([]);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // requestString can be a dep if accessCode or pageId can change

  // Your existing modal logic
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setInModal(false);
        setModalSrc("");
      }
    };
    if (inModal) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [inModal]);

  const handlePhotoClick = ({
    photo,
  }: {
    photo: PhotoGalleryItem;
    index: number;
  }) => {
    // For your existing modal:
    setModalSrc(photo.src);
    setModalText(photo.message || "");
    setInModal(true);
  };

  const getGallery = () => {
    if (photos === null) {
      return (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
        </div>
      );
    }
    if (photos.length === 0) {
      return (
        <div className={styles.noPhotos}>
          No photos found in the gallery.{" "}
          <a
            href={`https://www.facebook.com/${facebookPageId}/photos`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Facebook
          </a>
        </div>
      );
    }

    if (photos && !inModal) {
      return (
        <PhotoAlbum
          photos={photos}
          layout="rows" // or "columns" or "masonry"
          targetRowHeight={250} // Adjust for desired row height
          onClick={handlePhotoClick}
          // Add spacing, padding as per react-photo-album docs if needed
          spacing={(containerWidth) => (containerWidth < 600 ? 5 : 10)}
          padding={(containerWidth) => (containerWidth < 600 ? 5 : 10)}
        />
      );
    } else if (inModal) {
      // Your existing modal
      return (
        <div
          className={styles.modalWrapper}
          onClick={() => {
            // Click on backdrop closes modal
            setInModal(false);
            setModalSrc("");
            setModalText("");
          }}
        >
          <div
            className={styles.modalContent}
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            {" "}
            {/* Prevent click inside modal from closing it */}
            <button
              className={styles.closeModalButton}
              onClick={() => setInModal(false)}
            >
              &times;
            </button>
            <img
              src={modalSrc}
              alt={modalText || "Gallery image"}
              className={styles.modalImage}
            />
            {modalText && (
              <div className={styles.modalMessage}>{modalText}</div>
            )}
          </div>
        </div>
      );
    }
    // Should not be reached if loading/empty states are handled
    return <div className={styles.loading}>Error displaying gallery.</div>;
  };

  return (
    <div className={styles.galleryBackground}>
      <Header linksStyle="" />
      <div className={styles.main}>
        <Title text={"Our Work & Moments"} size={28} /> {/* Changed title */}
        <div className={styles.album}>{getGallery()}</div>
      </div>
      {/* Example for yet-another-react-lightbox
      {lightboxOpen && photos && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={photos.map(p => ({ src: p.src, title: p.message }))}
          index={lightboxIndex}
        />
      )}
      */}
      <Footer />
    </div>
  );
};
